import Vue from 'vue';
import { SOCKET_CHAT } from 'Root/plugins/socket/SocketAdapter';
// import { Events, MessageState } from 'Models/ChatModel';
import { Events } from 'Models/ChatModel';
import ClientChatRoom from './ClientChatRoom.class';

class ClientChat {
  /**
   * @param socket {SocketAdapter}
   * @param user {{id: Number, role: Number}}
   */
  constructor(socket, user) {
    this.rooms = {};
    this.socket = socket;
    this.user = {
      id: user.id,
      role: user.role,
    };
    this._serverActive = true;

    this.isConnectError = false;

    this.registerEvents();
  }

  /**
   * Регистрирует обработчики событий сокета
   */
  registerEvents() {
    const manager = this.socket.providers.chat.io;

    manager.on(Events.RECONNECT, () => {
      Object.values(this.rooms).forEach((room) => room.join());
    });

    this.listenEvent(Events.JOINED, this.joinedHandler);
    this.listenEvent(Events.LEAVED, this.leavedHandler);
    this.listenEvent(Events.MESSAGE, this.messageHandler);
    // this.listenEvent(Events.NEW_MESSAGE);
    this.listenEvent(Events.MESSAGE_STATE, this.messageStateHandler);
    this.listenEvent(Events.HISTORY, this.historyHandler);
    this.listenEvent(Events.USERS_LIST, this.usersListHandler);
    this.listenEvent(Events.SHUTDOWN, this.shutdownHandler);
    // this.listenEvent(Events.USER_STATUS, this.userStatusHandler);
    this.listenEvent(Events.CONNECT_ERROR, this.connectError);
    this.listenEvent(Events.RECONNECT, this.reconnect);
  }

  connectError() {
    this.isConnectError = true;
  }

  reconnect() {
    this.isConnectError = false;
  }

  shutdownHandler() {
    this._serverActive = false;
  }

  /**
   * Возвращает существующую комнату чата или создает новую
   * @param roomId {String} - ID комнаты чата, совпадает с ID заказа
   * @returns {ClientChatRoom}
   */
  getRoom(roomId) {
    if (roomId in this.rooms) {
      if (!this.rooms[roomId]._joined) {
        this.rooms[roomId].join();
      }

      return this.rooms[roomId];
    }

    const newRoom = new ClientChatRoom(roomId, this);
    Vue.set(this.rooms, roomId, newRoom);
    return newRoom;
  }

  /**
   * Возвращает массив комнат чата
   * @param roomIds {Array} - массив из ID комнат чата
   * @param chatTypeId {Number} - тип чата, если указать корректно, то вернет ClientTypedChat, иначе ClientChatRoom
   * @returns {ClientChatRoom[]|ClientTypedChat[]}
   */
  getRooms(roomIds, chatTypeId = null) {
    const rooms = [];

    roomIds.forEach((id) => {
      const room = this.getRoom(id);
      const roomToPush = room.typedChats[chatTypeId] || room;
      rooms.push(roomToPush);
    });

    return rooms;
  }

  /**
   * Добавляет обработчик на событие чата
   * @param event {string} - Название события ChatModel.Events
   * @param handler {Function} - Функция-обработчик
   */
  listenEvent(event, handler) {
    this.socket.addListener(SOCKET_CHAT, event, handler.bind(this));
  }

  listenEventOnce(event, handler) {
    this.socket.listenOnce(SOCKET_CHAT, event, handler);
  }

  /**
   * Отправляет событие event в неймспейс чата с нагрузкой data
   * @param event {string} - Название события
   * @param data {*} - Передаваемые данные
   */
  emitToChat(event, data) {
    if (!this._serverActive) {
      return;
    }

    this.socket.sendMessage(SOCKET_CHAT, event, data);
  }

  /**
   * Обработчик события: юзер подключился к комнате
   * @param roomId {string} - ID комнаты
   * @param isManager {boolean} - вью мод с админки
   */
  joinedHandler({ roomId, isManager }) {
    const room = this.getRoom(roomId);
    if (!isManager) {
      room.getHistory(); // если заходит менеджер, то история запросится в Chat.vue
    }

    room.getUsers();
  }

  /**
   * Обработчик события: изменение списка пользователей
   * @param roomId {string} - ID комнаты
   * @param users {{customer: {id: Number, online: boolean}, executor: {id: Number, online: boolean}}}
   * Объект статуса пользователей
   */
  usersListHandler({ roomId, users }) {
    const room = this.getRoom(roomId);
    room.setUsers(users);
  }

  /**
   * Обработчик события: юзер покинул комнату
   * @param roomId {string} - ID комнаты
   * @param userId {Number} - ID пользователя
   */
  leavedHandler({ roomId, userId }) {
    this.getRoom(roomId).removeUser(userId);
  }

  /**
   * Обработчик события: получено сообщение
   * @param message {ChatMessage} - Объект сообщения
   */
  messageHandler(message) {
    const { roomId } = message;
    this.getRoom(roomId).addMessage(message);
    // когда сообщение прилетело из другой вкладки
    // const isPassive = this.user.id === message.senderId;
    //
    // if (!isPassive) {
    //   this.emitToChat(Events.MESSAGE_STATE, {
    //     messageHash: message.hash,
    //     roomId,
    //     state: MessageState.READ.id,
    //   });
    // }
  }

  /**
   * Обработчик события: смена статуса сообщения
   * @param roomId {string} - ID комнаты чата
   * @param messageHash {string} - Hash сообщения
   * @param state {Number} - ID статуса ChatModel.MessageState
   * @param isOk {Boolean} - True если сообщение не содержит стоп-слов
   */
  messageStateHandler({
    roomId,
    messageHash,
    state,
    isOk,
  }) {
    this.getRoom(roomId).setMessageState(messageHash, state, isOk);
  }

  /**
   * Обработчик события: получена история комнаты чата
   * @param messages {ChatMessage[]} - Массив объектов сообщений
   */
  historyHandler(messages) {
    if (!messages.length) {
      return;
    }

    const room = this.getRoom(messages[0].roomId);
    messages.forEach((message) => {
      room.addMessage(message);
    });
  }

  /**
   * Обработчик события: изменение статуса юзера
   * @param id {Number} - ID юзера
   * @param online {Boolean} - Статус онлайна
   */
  // userStatusHandler({ id, online }) {
  //   Object.values(this.rooms).forEach((room) => {
  //     const user = Object.values(room.users).find((roomUser) => roomUser.id === id);
  //
  //     if (user) {
  //       user.online = online;
  //     }
  //   });
  // }
}

export default ClientChat;
